import React, { useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from '@iconify/react/dist/iconify.js';
import { API_URL } from "..";

const OpenTicketPage = () => {
    const [showAlertMessage, setShowAlertMessage] = useState({
        status: false,
        message: "",
        desc: "",
    });
    
    const [selectedFiles, setSelectedFiles] = useState([]);
    
    // Handle file selection
    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        const validTypes = [
            'image/jpeg', 
            'image/png', 
            'image/gif',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];
        
        const validFiles = files.filter(file => validTypes.includes(file.type));
        
        const filesWithPreview = validFiles.map(file => ({
            file,
            preview: file.type.includes('image') ? URL.createObjectURL(file) : null,
            type: file.type
        }));
        
        setSelectedFiles(prev => [...prev, ...filesWithPreview]);
    };

    // Handle file removal
    const removeFile = (index) => {
        setSelectedFiles(prev => {
            const newFiles = [...prev];
            // Revoke object URL to prevent memory leaks
            if (newFiles[index].preview) {
                URL.revokeObjectURL(newFiles[index].preview);
            }
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const onFormSubmit = async (event) => {
        event.preventDefault();
        
        // Validation
        if (event.target.elements.subject.value === "") {
            setShowAlertMessage({
                message: 'error',
                status: true,
                desc: 'The subject field is required'
            });
            return false;
        } else if (!event.target.elements.department.value) {
            setShowAlertMessage({
                message: 'error',
                status: true,
                desc: 'The department field is required'
            });
            return false;
        } else if (event.target.elements.message.value === "") {
            setShowAlertMessage({
                message: 'error',
                status: true,
                desc: 'The message field is required'
            });
            return false;
        }

        // Prepare form data
        const user = JSON.parse(localStorage.getItem('user'));
        const formData = new FormData();
        formData.append('subject', event.target.elements.subject.value);
        formData.append('department', event.target.elements.department.value);
        formData.append('description', event.target.elements.message.value);
        formData.append('affiliate_id', user.id);
        
        // Append files
        selectedFiles.forEach((item) => {
            formData.append('documents', item.file);
        });

        try {
            // API call (replace with your actual API endpoint)
            const response = await fetch(API_URL+'/api/ticket/create', {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                setShowAlertMessage({
                    message: 'success',
                    status: true,
                    desc: 'Ticket submitted successfully'
                });
                // Reset form
                event.target.reset();
                setSelectedFiles([]);
            } else {
                throw new Error('Submission failed');
            }
        } catch (error) {
            setShowAlertMessage({
                message: 'error',
                status: true,
                desc: 'Failed to submit ticket. Please try again.'
            });
        }
    };

    return (
        <MasterLayout>
            <Breadcrumb title="Open Ticket" />

            <div className="row gy-4 justify-content-center">
                <div className="col-lg-8">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Open a new ticket</h5>
                        </div>
                        <div className="card-body">
                            <form className="row gy-3" onSubmit={onFormSubmit}>
                                <div className="col-md-12">
                                    <label htmlFor="subject" className="form-label">Subject</label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        className="form-control"
                                        placeholder="Enter your ticket subject.."
                                    />
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label">Select Department</label>
                                    <select name="department" className="form-control">
                                        <option value="">Select Department</option>
                                        <option value="Sale">Sale</option>
                                        <option value="Support">Support</option>
                                        <option value="Billing">Billing</option>
                                    </select>
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label">Message</label>
                                    <textarea 
                                        name="message" 
                                        className="form-control" 
                                        placeholder="Enter your message....." 
                                        rows={6}
                                    />
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label">Attach file(s)</label>
                                    <input 
                                        type="file" 
                                        className="form-control" 
                                        multiple
                                        accept="image/*,.pdf,.doc,.docx"
                                        onChange={handleFileChange}
                                    />
                                </div>

                                {/* File Preview Section */}
                                {selectedFiles.length > 0 && (
                                    <div className="col-md-12">
                                        <div className="row gy-2">
                                            {selectedFiles.map((item, index) => (
                                                <div key={index} className="col-md-3 position-relative">
                                                    {item.type.includes('image') ? (
                                                        <img 
                                                            src={item.preview} 
                                                            alt="preview" 
                                                            className="img-thumbnail"
                                                            style={{maxHeight: '100px', objectFit: 'cover'}}
                                                        />
                                                    ) : (
                                                        <div className="p-2 border rounded text-center">
                                                            <Icon 
                                                                icon="bi:file-earmark" 
                                                                className="text-muted" 
                                                                style={{fontSize: '2rem'}}
                                                            />
                                                            <p className="mb-0 small">{item.file.name}</p>
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-info btn-sm position-absolute top-0 end-0"
                                                        onClick={() => removeFile(index)}
                                                    >
                                                        <Icon icon="iconamoon:sign-times-light" className="icon" fontSize={22} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {showAlertMessage.status && (
                                    <div
                                        className={
                                            showAlertMessage.message === "error"
                                                ? "alert alert-danger bg-danger-50 text-danger-600 border-danger-50 px-24 py-11 mb-0 fw-semibold text-lg radius-8 d-flex align-items-center justify-content-between mb-10"
                                                : "alert alert-success bg-success-50 text-success-600 border-success-50 px-24 py-11 mb-0 fw-semibold text-lg radius-8 d-flex align-items-center justify-content-between mb-10"
                                        }
                                        role="alert"
                                    >
                                        {showAlertMessage.desc}
                                        <button
                                            type="button"
                                            onClick={() => setShowAlertMessage({status: false, message: "", desc: ""})}
                                            className="remove-button text-secondary-600 text-xxl line-height-1"
                                        >
                                            <Icon icon="iconamoon:sign-times-light" className="icon" />
                                        </button>
                                    </div>
                                )}
                                <div className="col-12">
                                    <button className="btn btn-primary-600" type="submit">
                                        Submit form
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </MasterLayout>
    );
};

export default OpenTicketPage;